/**
 * 素材统计页指标名称 — 根据 date_type 返回对应的 i18n key
 *
 * 规则（来自 v2 需求）：
 *   - KPI 卡片：单天 → 原名；日期范围 → "日均" 前缀；周 → "周日均"；月 → "月日均"
 *   - 图表 Segment / 标题：日 → 原名（无前缀）；周 → "周日均"；月 → "月日均"
 */  
import moment from 'moment';

/** 时间粒度 */
export type TDateType = 'daily' | 'weekly' | 'monthly';

export interface IFilterLike {
  date_type?: TDateType;
  start_time: string;
  end_time: string;
}

/**
 * KPI 卡片指标名称映射
 * Marketing的Competitor Ad Comparison的表头
*/
export const CARD_LABEL_MAP: Record<string, Record<DayMode, string>> = {
  'Active Ads': {
    single: 'Active Ads',
    daily_range: 'Avg. Daily Active Ads',
    weekly: 'Avg. Weekly Active Ads',
    monthly: 'Avg. Monthly Active Ads',
  },
  'Active Creatives': {
    single: 'Active Creatives',
    daily_range: 'Avg. Daily Active Creatives',
    weekly: 'Avg. Weekly Active Creatives',
    monthly: 'Avg. Monthly Active Creatives',
  },
  'New Creatives': {
    single: 'New Creatives',
    daily_range: 'Accumulated New Creatives',
    weekly: 'Accumulated Weekly New Creatives',
    monthly: 'Accumulated Monthly New Creatives',
  },   
  "New Ads": {
    single: "New Ads",
    daily_range: "Accumulated New Ads",
    weekly: 'Accumulated Weekly New Ads',
    monthly: 'Accumulated Monthly New Ads',
  }, 
  "Active Video Ads": {
    single: "Active Video Ads",
    daily_range: "Avg. Daily Active Video Ads",
    weekly: "Avg. Weekly Active Video Ads",
    monthly: "Avg. Monthly Active Video Ads",
  },
  "Active Image Ads": {
    single: "Active Image Ads",
    daily_range: "Avg. Daily Active Image Ads",
    weekly: "Avg. Weekly Active Image Ads",
    monthly: "Avg. Monthly Active Image Ads",
  },
  "Active Playable Ads": {
    single: "Active Playable Ads",
    daily_range: "Avg. Daily Active Playable Ads",
    weekly: "Avg. Weekly Active Playable Ads",
    monthly: "Avg. Monthly Active Playable Ads",
  },
};

/** 图表指标名称映射（Day 不加前缀） */
const CHART_LABEL_MAP: Record<string, Record<string, string>> = {
  'Active Ads': {
    daily: 'Active Ads',
    weekly: 'Avg. Weekly Active Ads',
    monthly: 'Avg. Monthly Active Ads',
  },
  'Active Creatives': {
    daily: 'Active Creatives',
    weekly: 'Avg. Weekly Active Creatives',
    monthly: 'Avg. Monthly Active Creatives',
  },
  'New Creatives': {
    daily: 'New Creatives',
    weekly: 'Accumulated Weekly New Creatives',
    monthly: 'Accumulated Monthly New Creatives',
  },
  'Total Engagements on Active Ads': {
    daily: 'Active Ads Engagements',
    weekly: 'Avg. Weekly Active Ads Engagements',
    monthly: 'Avg. Monthly Active Ads Engagements',
  },
  'New Ads': {
    daily: 'New Ads',
    weekly: 'Accumulated Weekly New Ads',
    monthly: 'Accumulated Monthly New Ads',
  },
  'Active Ads% by Channel': {
    daily: 'Active Ads% by Channel',
    weekly: 'Avg. Weekly Active Ads% by Channel',
    monthly: 'Avg. Monthly Active Ads% by Channel',
  },
  'Estimated Cumulative Heat': {
    daily: 'Estimated Cumulative Heat',
    weekly: 'Avg. Weekly Estimated Cumulative Heat',
    monthly: 'Avg. Monthly Estimated Cumulative Heat',
  },
  'Cumulative Impressions': {
    daily: 'Cumulative Impressions',
    weekly: 'Accumulated Weekly Cumulative Impressions',
    monthly: 'Accumulated Monthly Cumulative Impressions',
  },
  'Active Ads by Creative Type': {
    daily: 'Active Ads by Creative Type',
    weekly: 'Avg. Weekly Active Ads by Creative Type',
    monthly: 'Avg. Monthly Active Ads by Creative Type',
  },
  'Cumulative Impressions by Type': {
    daily: 'Cumulative Impressions by Type',
    weekly: 'Avg. Weekly Cumulative Impressions by Type',
    monthly: 'Avg. Monthly Cumulative Impressions by Type',
  },
  'Active Ads by Country': {
    daily: 'Active Ads by Country',
    weekly: 'Avg. Weekly Active Ads by Country',
    monthly: 'Avg. Monthly Active Ads by Country',
  },
  Download: {
    daily: 'Downloads',
    weekly: 'Downloads',
    monthly: 'Downloads',
  },
  DAU: {
    daily: 'DAU',
    weekly: 'WAU',
    monthly: 'MAU',
  },
  'Avg. Active Ads': {
    daily: 'Avg. Daily Active Ads',
    weekly: 'Avg. Weekly Active Ads',
    monthly: 'Avg. Monthly Active Ads',
  },
};

function resolveGranularity(filter?: IFilterLike): string {
  if (!filter) return 'daily';
  const dateType = filter.date_type;
  if (dateType === 'weekly') return 'weekly';
  if (dateType === 'monthly') return 'monthly';
  return 'daily';
}

/**
 * KPI 卡片标题：考虑单天 vs 日期范围
 * @deprecated Use getCardMetricLabelKey instead for useLanguage support
 */
export function getCardMetricLabel(
  baseKey: string,
  filter?: IFilterLike,
): string {
  return getCardMetricLabelKey(baseKey, filter);
}

/**
 * KPI 卡片标题 i18n key：考虑单天 vs 日期范围
 */
export function getCardMetricLabelKey(
  baseKey: string,
  filter?: IFilterLike,
): string {
  const map = CARD_LABEL_MAP[baseKey];
  if (!map) return baseKey;
  if (!filter) return map.single;
  const dayMode = getDayMode(filter.date_type || 'daily', filter.start_time, filter.end_time);
  return map[dayMode];
}

/**
 * 图表 Segment / 标题：Day 无前缀，Week/Month 加前缀
 * @deprecated Use getChartMetricLabelKey instead for useLanguage support
 */
export function getChartMetricLabel(
  baseKey: string,
  filter?: IFilterLike,
): string {
  return getChartMetricLabelKey(baseKey, filter);
}

/**
 * 图表 Segment / 标题 i18n key：Day 无前缀，Week/Month 加前缀
 */
export function getChartMetricLabelKey(
  baseKey: string,
  filter?: IFilterLike,
): string {
  const map = CHART_LABEL_MAP[baseKey];
  if (!map) return baseKey;
  return map[resolveGranularity(filter)] || baseKey;
}




// ─────────────────────────────────────────────────────────
// DayMode：细粒度日期模式，用于区分不同时间粒度的文案
// ─────────────────────────────────────────────────────────

/** 日期模式类型 */
export type DayMode = 'single' | 'daily_range' | 'weekly' | 'monthly';

/**
 * 根据筛选条件获取细粒度日期模式
 *
 * @param dateType  时间粒度（daily / weekly / monthly / hourly）
 * @param startTime 开始时间 yyyy-MM-dd
 * @param endTime   结束时间 yyyy-MM-dd
 * @returns DayMode
 *   - single：单天（daily 且起止同一天）
 *   - daily_range：日范围（daily 且起止不同天）
 *   - weekly：周粒度
 *   - monthly：月粒度
 */
export function getDayMode(
  dateType: string,
  startTime?: string,
  endTime?: string,
): DayMode {
  if (dateType === 'weekly') return 'weekly';
  if (dateType === 'monthly') return 'monthly';
  // dateType === 'daily' | 'hourly'：判断是否同一天
  if (startTime && endTime) {
    return moment(startTime).format('YYYY-MM-DD') === moment(endTime).format('YYYY-MM-DD')
      ? 'single'
      : 'daily_range';
  }
  return 'daily_range';
}
